import { AddPhotoAlternate, Upload } from "@mui/icons-material";
import { Box, Button, Container, ImageList, ImageListItem, Modal, Typography } from "@mui/material";
import { VisuallyHiddenFileInput } from "./VisuallyHiddenFileInput";
import { FC, useEffect, useState } from "react";
import { getAllImagesFromFolder } from "../../services/firebaseService";
import DividerHorizontal from "../dividers/DividerHorizontal";
import { useCustomThemeContext } from "../../providers/customThemeProvider";

interface ImageFormListProps {
  defaultValue?: File | string;
  onImageSelect: (image: File | string) => void;
  folder: string;
  label: string;
  iconsOnly?: boolean;
}

export const ImagePicker: FC<ImageFormListProps> = ({ defaultValue, onImageSelect, folder, label, iconsOnly = false }) => {
  const { theme } = useCustomThemeContext();
  const [images, setImages] = useState<(File | string)[]>([]);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [selectedImage, setSelectedImage] = useState<File | string>(defaultValue || "");

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    border: 5,
    BorderStyle: "solid",
    borderColor: theme.palette.primary.main,
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
  };

  useEffect(() => {
    getAllImagesFromFolder(folder).then((fetchedImages) => {
      setImages(fetchedImages);
    }).catch((error) => {
      console.error("Error fetching images:", error);
    });
  }, [folder]);

  //only send image out when selection is saved in usestate
  useEffect(() => {
    onImageSelect(selectedImage);
  }
    , [selectedImage]);

  const handleChosenImage = (image: File | string): File | string => {
    setSelectedImage(image);
    onImageSelect(image);
    handleClose();
    return image;
  }

  const handleImageUpload = (file: File | null) => {
    if (file) {
      setImages([...images, file]);
    }
  }

  return (
    <Container disableGutters
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: 1,
        borderColor: "grey.300",
        py: 2
      }}>
      <Box display="flex" flex={"33%"} justifyContent={"flex-start"}><Typography component="span">{label}: </Typography></Box>
      {selectedImage && (
        <Box display="flex" flex={"33%"} justifyContent={"center"}>
          <img
            src={typeof selectedImage === 'string' ? selectedImage : URL.createObjectURL(selectedImage)}
            alt={`Selected picture for ${folder.toLowerCase()}.`}
            style={{ maxHeight: 167, maxWidth: 250, objectFit: "contain" }}
          />
        </Box>
      )}

      <Button
        variant="contained"
        onClick={handleOpen}><AddPhotoAlternate />Pick image</Button>
      <Modal
        keepMounted
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...modalStyle, maxHeight: '40vh', overflowY: 'auto', pr: 2 }}>
          <Container disableGutters sx={{ justifyContent: "space-between", display: "flex", alignItems: "center", mb: 2 }}>
            <Box display="flex" flex={"33%"} justifyContent={"flex-start"}>
              <Typography variant="h6">Pick an image: </Typography>
            </Box>
            <Box display="flex" flex={"33%"} justifyContent={"flex-end"}>
              <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<Upload />}
              >
                Upload image
                <VisuallyHiddenFileInput
                  type="file"
                  accept={iconsOnly ? ".ico" : "image/*"}
                  onChange={(event) => {
                    handleImageUpload(event.target.files?.[0] || null);
                  }}
                />
              </Button>
            </Box>
          </Container>

          <DividerHorizontal />

          {images && images.length === 0 &&
            <Container disableGutters sx={{ display: "flex", justifyContent: "center", alignContent: "center", mt: 2, height: "100%" }}>
              <Typography>There are no images available yet, try uploading a new image.</Typography>
            </Container>}

          {images && images.length > 0 &&
            <ImageList sx={{ maxWidth: "fit-content", maxHeight: "fit-content" }} cols={3}>
              {images.map((image, index) => (
                <Box key={index} display="flex" justifyContent="center" >
                  <Button onClick={() => handleChosenImage(image)}>
                    <ImageListItem key={index}>
                      <img
                        style={{ maxHeight: 167, maxWidth: 250, objectFit: "contain" }}
                        srcSet={typeof image === 'string' ? image : URL.createObjectURL(image)}
                        src={typeof image === 'string' ? image : URL.createObjectURL(image)}
                        alt={"optional picture for" + folder}
                        loading="lazy"
                      />
                    </ImageListItem>
                  </Button>
                </Box>
              ))}
            </ImageList>}
        </Box>
      </Modal>
    </Container>
  );
}

export default ImagePicker;